import { latLonToEN } from '../ol-utils';
import { Ajax } from '@shared/Ajax';
import { Events } from '@shared/Events';
import OlMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Coordinate } from 'ol/coordinate';
import { Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';

const FEATURE_TYPE = 'alignment-stations';

const stationZoomDistances = [1, 1, 2, 5, 10, 10, 10, 10, 10, 50, 100, 250, 500, 1000, 1000];

const source: VectorSource = new VectorSource({
    features: [],
    useSpatialIndex: true,
});

const layer: VectorLayer<any> = new VectorLayer({
    source,
    updateWhileInteracting: true,
});

const styleCache = {};

let activePromise: JQueryPromise<any> = null;
const activeAlignment = {
    modelId: -1,
    index: 0,
};
let activeAlignmentStationsStep = 10;
let activeAlignmentStations = [];

function getStyle(station: string): olStyle.Style {
    if (styleCache[station]) return styleCache[station];

    const style = new olStyle.Style({
        image: new olStyle.Circle({
            radius: 3,
            fill: new olStyle.Fill({
                color: '#ff0000',
            }),
        }),
        text: new olStyle.Text({
            text: station,
            font: '12px sans-serif',
            offsetY: -12,
            textAlign: 'center',
            textBaseline: 'bottom',
            fill: new olStyle.Fill({
                color: '#ff0000',
            }),
            stroke: new olStyle.Stroke({
                color: '#fff',
                width: 2,
            }),
        }),
    });

    styleCache[station] = style;
    return style;
}

function drawOnMapForAlignment(alignment: IAlignment, map: OlMap, zIndex = 100) {
    const view = map.getView();
    const zoom = Math.round(view.getZoom());
    clear();
    if (zoom < 14 || !alignment) {
        return;
    }
    const maxZoom = view.getMaxZoom();
    const zoomIndex = maxZoom - zoom;
    let everyNmeters = 10000;

    if (zoomIndex < stationZoomDistances.length) {
        everyNmeters = stationZoomDistances[zoomIndex];
    }

    const startStation = Math.round(alignment.startStation);
    const firstTickM = Math.ceil(startStation / everyNmeters) * everyNmeters;

    if (alignment.modelId != activeAlignment.modelId || alignment.index !== activeAlignment.index) {
        activeAlignment.modelId = alignment.modelId;
        activeAlignment.index = alignment.index;

        fetchStations(alignment.modelId, alignment.index).done(resp => {
            activeAlignmentStationsStep = resp.step ?? 10;
            activeAlignmentStations = resp.coords.length ? resp.coords : resp.stationEquations;
            drawOnMapForAlignment(alignment, map, zIndex);
        });
        Events.trigger('alignment_stations_feature_factory_changed', { alignment, activeAlignmentStations });
        return;
    }

    if (!activeAlignmentStations || !activeAlignmentStations.length) {
        return;
    }

    // station equations are sent for every n-th/step meter, while regular integer stations are sent for every meter
    const isEquation = typeof activeAlignmentStations[0].station === 'string';

    if (isEquation) {
        activeAlignmentStations.forEach((station, i) => {
            if (i % (everyNmeters / activeAlignmentStationsStep) === 0) {
                drawTickMark(latLonToEN(station.lat, station.lon), station.station);
            }
        });
    } else {
        let currStationM = firstTickM - everyNmeters;
        while (currStationM < alignment.endStation + everyNmeters) {
            const stationArrayIndex = utils.clamp(0, currStationM - startStation, activeAlignmentStations.length - 1);
            if (!activeAlignmentStations[stationArrayIndex]) {
                return;
            }
            if (!activeAlignmentStations[stationArrayIndex].lat || !activeAlignmentStations[stationArrayIndex].lon) {
                console.warn('Alignment stations - no coordinates for station', stationArrayIndex);
                return;
            }
            const coord = latLonToEN(activeAlignmentStations[stationArrayIndex].lat, activeAlignmentStations[stationArrayIndex].lon);
            const meters = startStation + stationArrayIndex;
            drawTickMark(coord, meters);
            currStationM += everyNmeters;
        }
    }
    Events.trigger('alignment_stations_feature_factory_changed', { alignment, activeAlignmentStations });

    try {
        map.addLayer(layer);
        layer.setZIndex(zIndex);
    } catch {}
}

function drawTickMark(coord: Coordinate, station: number | string) {
    const f = makeTickMarkFeature(coord, station);
    f.setStyle(getStyle(station + ''));
    try {
        source.addFeature(f);
    } catch {}
}

const makeTickMarkFeature = _.memoize(
    (coord: Coordinate, station: number | string) => {
        if (!coord) {
            console.log('bad coords');
            return;
        }

        return new Feature({
            geometry: new OlPoint(coord),
        });
    },
    (coord: Coordinate, station: number) => coord[0] + '_' + coord[1] + '_' + station
);

function fetchStations(alignmentId: number, alignmentIndex: number) {
    if (!alignmentId) return;

    if (activePromise) {
        (activePromise as JQueryXHR).abort();
        activePromise = null;
    }

    activePromise = Ajax({
        url: 'ajax_alignment_stations.json',
        data: {
            alignmentId,
            alignmentIndex,
            projectId: kuura.getProjectId(),
        },
        cache: false,
        cacheTimeoutS: 120,
    }).always(() => {
        activePromise = null;
    });

    return activePromise;
}

export function clear() {
    source.clear(true);
}

export const AlignmentStationsLayer = {
    drawOnMapForAlignment,
    clear,
    FEATURE_TYPE,
};
